import { useEffect, useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, AlertTriangle, Mail, Phone, MapPin } from 'lucide-react';
import { HOME } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  interest: 'general',
  message: '',
};

export default function Enquiry() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // Listen for prefill requests dispatched by ProductGrid "Enquire about this"
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setForm((p) => ({
        ...p,
        subject: detail.subject ?? p.subject,
        interest: detail.interest ?? p.interest,
      }));
    };
    window.addEventListener('ecobike:prefill-enquiry', handler);
    return () => window.removeEventListener('ecobike:prefill-enquiry', handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'sending', message: '' });
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus({
        state: 'success',
        message: 'Thank you. We’ll be in touch within one working day.',
      });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: 'error',
        message:
          typeof detail === 'string'
            ? detail
            : 'Sorry, your enquiry could not be sent. Please try again or email us directly.',
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={HOME.contactSection}
      className="section bg-[var(--brand-parchment-warm)] relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-5 flex flex-col justify-between gap-10">
            <div>
              <span className="font-eyebrow">Get in touch · The estate replies</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] leading-[1.02] tracking-tight">
                Send us a note.<br />
                <span className="italic">The Gamekeeper replies.</span>
              </h2>
              <p className="mt-6 text-[var(--brand-muted)] text-lg leading-relaxed max-w-md">
                Bike fit, adaptive consult, wholesale, or a straight question about
                which kit will actually last your trip &mdash; drop us a line. No call
                centre, no scripts, no auto-responder. One reply from the founder,
                inside a working day.
              </p>
            </div>

            <ul className="space-y-5 text-sm">
              <li className="flex items-start gap-3 text-[var(--brand-ink)]">
                <MapPin size={18} className="mt-0.5 text-[var(--brand-olive)]" />
                <span>
                  Online only — based on the west coast<br />
                  Ayrshire, Scotland · United Kingdom
                </span>
              </li>
              <li className="flex items-center gap-3 text-[var(--brand-ink)]">
                <Mail size={18} className="text-[var(--brand-olive)]" />
                <a href="mailto:hello@ecobikecampingsuppliesco.com" className="link-expand">
                  hello@ecobikecampingsuppliesco.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-[var(--brand-ink)]">
                <Phone size={18} className="mt-0.5 text-[var(--brand-olive)]" />
                <span>
                  Calls answered by our reception service.<br />
                  <span className="text-[var(--brand-muted)] text-xs">
                    Drop us a note below and we’ll call you back within one working day.
                  </span>
                </span>
              </li>
            </ul>
          </aside>

          <form
            onSubmit={submit}
            data-testid={HOME.enquiryForm}
            className="lg:col-span-7 bento p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Full name *
                </label>
                <input
                  data-testid={HOME.enquiryName}
                  className="field"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  maxLength={120}
                  placeholder="Hamish Macdonald"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Email *
                </label>
                <input
                  data-testid={HOME.enquiryEmail}
                  className="field"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder="you@example.co.uk"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Phone <span className="opacity-60 normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  data-testid={HOME.enquiryPhone}
                  className="field"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+44 7..."
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Interest
                </label>
                <select
                  data-testid={HOME.enquiryInterest}
                  className="field"
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                >
                  <option value="general">General</option>
                  <option value="e-bikes">E-Bikes</option>
                  <option value="camping">Camping Supplies</option>
                  <option value="adaptive">Adaptive Cycling</option>
                  <option value="wholesale">Wholesale / Trade</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                Subject *
              </label>
              <input
                data-testid={HOME.enquirySubject}
                className="field"
                name="subject"
                value={form.subject}
                onChange={onChange}
                required
                maxLength={160}
                placeholder="Adaptive bike fitting in March"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                Message *
              </label>
              <textarea
                data-testid={HOME.enquiryMessage}
                className="field"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                required
                maxLength={4000}
                placeholder="Tell us a little about what you’re after — trails, terrain, riders, kit..."
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
              <button
                data-testid={HOME.enquirySubmit}
                type="submit"
                disabled={status.state === 'sending'}
                className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-none disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === 'sending' ? 'Sending\u2026' : 'Send Enquiry'}
                <Send size={15} />
              </button>
              <p className="text-xs text-[var(--brand-muted)]">
                We respect your privacy. No marketing, ever.
              </p>
            </div>

            {status.state === 'success' && (
              <div
                data-testid={HOME.enquirySuccess}
                className="flex items-start gap-3 bg-[var(--brand-olive)]/10 border border-[var(--brand-olive)]/30 p-4 text-sm text-[var(--brand-forest)]"
              >
                <CheckCircle2 size={18} className="mt-0.5 text-[var(--brand-olive)]" />
                <span>{status.message}</span>
              </div>
            )}
            {status.state === 'error' && (
              <div
                data-testid={HOME.enquiryError}
                className="flex items-start gap-3 bg-red-50 border border-red-200 p-4 text-sm text-red-800"
              >
                <AlertTriangle size={18} className="mt-0.5" />
                <span>{status.message}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
